import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Modal } from '../../common/Modal';
import Button from '../../common/Button';
import FormField, { Input, Select, Textarea } from '../../common/FormField';
import { formatQuantity, effectiveQuantity } from '../../../lib/quantity';
import { messageFromError } from './errorMessage';

// Create/edit form for one canned job. Mount it only while open: its state
// is seeded from `job` once, so a fresh mount is a fresh form.

let nextKey = 0;
const blankLine = () => ({
  key: `new-${nextKey++}`, part_name: '', part_number: '', quantity: '1', unit_cost: '', condition: 'new',
});

const fromPart = (p) => ({
  key: `p-${nextKey++}`,
  part_name: p.part_name || '',
  part_number: p.part_number || '',
  quantity: formatQuantity(p.quantity),
  unit_cost: p.unit_cost === 0 || p.unit_cost ? String(p.unit_cost) : '',
  condition: p.condition || 'new',
});

// 2 decimals, half up (the server rejects more than 2).
const money = (value) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(Number((n * 100).toPrecision(12))) / 100;
};

const isEmptyLine = (l) => !l.part_name.trim() && !l.part_number.trim() && !String(l.unit_cost).trim();

const CannedJobEditor = ({ isOpen, job = null, onClose, onSave }) => {
  const [name, setName] = useState(job?.name || '');
  const [lines, setLines] = useState(() => (job?.parts || []).map(fromPart));
  const [hours, setHours] = useState(job ? String(job.labor_hours ?? 0) : '');
  const [work, setWork] = useState(job?.work_description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lineErrors, setLineErrors] = useState({});

  const setLine = (key, patch) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const handleSave = async () => {
    const errors = {};
    const parts = [];
    lines.forEach((l) => {
      if (isEmptyLine(l)) return;
      if (!l.part_name.trim()) {
        errors[l.key] = 'Give this part a name';
        return;
      }
      parts.push({
        part_name: l.part_name.trim(),
        part_number: l.part_number.trim() || null,
        quantity: effectiveQuantity(l.quantity),
        unit_cost: money(l.unit_cost),
        condition: l.condition,
      });
    });
    setLineErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setError('');
    try {
      await onSave({
        name: name.trim(),
        parts,
        labor_hours: money(hours),
        work_description: work.trim() || null,
      });
    } catch (err) {
      setError(messageFromError(err, 'Could not save this canned job.'));
      setSaving(false);
      return;
    }
    // Success: the parent closes (unmounts) this editor.
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      closeOnOverlayClick={false}
      title={job ? 'Edit canned job' : 'New canned job'}
      size="lg"
    >
      <div className="space-y-4">
        <FormField label="Name" htmlFor="canned-name" required>
          <Input
            id="canned-name"
            type="text"
            maxLength={120}
            placeholder="Oil change"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormField>

        <div className="space-y-3">
          <p className="text-body-strong text-ink">Parts</p>
          {lines.map((l, i) => (
            <div key={l.key} className="rounded-control border border-border-default p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Part name" htmlFor={`${l.key}-name`}>
                  <Input
                    id={`${l.key}-name`}
                    type="text"
                    maxLength={200}
                    value={l.part_name}
                    onChange={(e) => setLine(l.key, { part_name: e.target.value })}
                  />
                </FormField>
                <FormField label="Part number" htmlFor={`${l.key}-num`}>
                  <Input
                    id={`${l.key}-num`}
                    type="text"
                    maxLength={100}
                    value={l.part_number}
                    onChange={(e) => setLine(l.key, { part_number: e.target.value })}
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <FormField label="Qty" htmlFor={`${l.key}-qty`}>
                  <Input
                    id={`${l.key}-qty`}
                    type="number"
                    inputMode="decimal"
                    min="0.01"
                    step="0.01"
                    value={l.quantity}
                    onChange={(e) => setLine(l.key, { quantity: e.target.value })}
                  />
                </FormField>
                <FormField label="Unit cost" htmlFor={`${l.key}-cost`}>
                  <Input
                    id={`${l.key}-cost`}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={l.unit_cost}
                    onChange={(e) => setLine(l.key, { unit_cost: e.target.value })}
                  />
                </FormField>
                <FormField label="Condition" htmlFor={`${l.key}-cond`} className="col-span-2 sm:col-span-1">
                  <Select
                    id={`${l.key}-cond`}
                    value={l.condition}
                    onChange={(e) => setLine(l.key, { condition: e.target.value })}
                  >
                    <option value="new">New</option>
                    <option value="used">Used</option>
                    <option value="rebuilt">Rebuilt</option>
                  </Select>
                </FormField>
              </div>
              {lineErrors[l.key] && (
                <p className="text-small text-danger">{lineErrors[l.key]}</p>
              )}
              <Button
                variant="ghost"
                size="sm"
                icon={Trash2}
                aria-label={`Remove part ${i + 1}${l.part_name.trim() ? ` (${l.part_name.trim()})` : ''}`}
                onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            icon={Plus}
            onClick={() => setLines((prev) => [...prev, blankLine()])}
          >
            Add part
          </Button>
        </div>

        <FormField label="Labour hours" htmlFor="canned-hours">
          <Input
            id="canned-hours"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.1"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </FormField>

        <FormField label="Work to be done" htmlFor="canned-work">
          <Textarea
            id="canned-work"
            rows={3}
            maxLength={1000}
            value={work}
            onChange={(e) => setWork(e.target.value)}
          />
        </FormField>

        {error && (
          <p role="alert" className="text-small text-danger">{error}</p>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            loading={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CannedJobEditor;
